import { useState } from 'react';
import { arcOfIssue, wherePreview } from '../../../lib/s4/cover';
import {
  checkpointDefaults,
  coverDefaults,
  issueCastDefaults,
  issueStoryDefaults,
  locationRuleDefaults,
  storyRewardsDefaults,
} from '../../../lib/s4/defaults';
import {
  STORY_LOCATION_KINDS,
  STORY_PICK_NEAR,
  type ArcDef,
  type CoverVariantDef,
  type IssueCastDef,
  type IssueStoryDef,
  type LocationRuleDef,
  type StoryBeatVariantDef,
  type StoryCheckpointDef,
} from '../../../lib/s4/types';
import type { PanelSpec } from '../../../lib/story/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import type { FieldErrors, MissionDef } from '../../../lib/validate';
import { CellEditor, type CellContext } from '../../story/CellEditor';
import { ConditionPicker } from '../../story/ConditionPicker';
import { relErrors } from '../../story/relErrors';
import {
  CheckboxField,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  SelectField,
  StackListEditor,
  StringListEditor,
  TextField,
  smallBtn,
} from '../fields';
import { useContentBundle, type Option } from '../refs';
import { CoverPreview } from './CoverPreview';
import { LoadoutEditor } from './LoadoutEditor';

const opts = (list: readonly string[]): Option[] => list.map((v) => ({ value: v, label: v }));
const B = 'story';

/** A blank cover panel: the hero standing in the issue's set. */
const newCoverPanel = (lead: string): PanelSpec => ({
  id: 'cover',
  layout: { slot: 'full' },
  shot: { type: 'medium' },
  actors: lead ? [{ role: lead, pose: 'stand', facing: 'camera' }] : [],
  set: { kind: 'scenario', template: '', dressing: true },
  effects: [],
  dialogue: [],
});

/**
 * The mission editor's **Story** tab (story.html "Admin"; shared-cs `IssueStoryDef`): the arc and number, cast (the hero plus who comes
 * along, each by condition), the hero's loadout, the location rule, the cover (a panel spec, variants by ledger condition, and the preview),
 * intro and outro beat variants, dog-ears on the scenario's objectives (or a stage start), and rewards. Issues are the mission's, keyed
 * `story.…`. A mission without a story block isn't a story issue; "Make this a story issue" adds one.
 */
export function IssueStoryEditor({
  mission,
  onChange,
  errors,
}: {
  mission: MissionDef;
  onChange: (story: IssueStoryDef | null) => void;
  errors: FieldErrors;
}) {
  const bundle = useContentBundle();
  const [previewPanel, setPreviewPanel] = useState(-1);
  const raw = mission.story;
  if (raw == null) {
    const arc = arcOfIssue(bundle, mission.id ?? '', null);
    return (
      <div className="rounded border-2 border-dashed border-ink p-4 text-sm">
        <p>
          This mission isn&rsquo;t a story issue. A story issue has no trigger, is played by its
          arc&rsquo;s fixed hero, and lives on the Comics shelf (ADR 0042).
        </p>
        <button
          type="button"
          className={`${smallBtn} mt-2`}
          onClick={() => onChange(issueStoryDefaults(arc?.id ?? ''))}
        >
          Make this a story issue
        </button>
        <IssueList issues={errors[B]} />
      </div>
    );
  }
  const s = withDefaults(issueStoryDefaults(), raw) as Required<IssueStoryDef>;
  const set = (patch: Partial<IssueStoryDef>) => onChange({ ...raw, ...patch });
  const at = (p: string) => errors[`${B}.${p}`];

  const arcs = ((bundle.arcs ?? []) as ArcDef[]).filter((a) => a?.id);
  const arc = arcs.find((a) => a.id === s.arcId);
  const castIds = (arc?.cast ?? []).map((c) => c.id ?? '').filter(Boolean);
  const castOpts = (arc?.cast ?? []).map((c) => ({
    value: c.id ?? '',
    label: `${c.name || c.id} (${c.id})`,
  }));
  const chainOpts = (bundle.beatChains ?? []).flatMap((c) =>
    c?.id ? [{ value: c.id, label: `${c.title || c.id} (${c.id})` }] : [],
  );
  const cosmeticOpts = (bundle.cosmetics ?? []).flatMap((c) =>
    c?.id ? [{ value: c.id, label: `${c.name || c.id} (${c.id}) · ${c.kind ?? ''}` }] : [],
  );
  const flagOpts = (bundle.worldFlags ?? []).flatMap((f) =>
    f?.key ? [{ value: f.key, label: f.key }] : [],
  );
  const arcIssueOpts = (arc?.issues ?? [])
    .filter((id) => id !== mission.id)
    .map((id) => {
      const m = bundle.missions?.find((x) => x?.id === id);
      return { value: id, label: `#${m?.story?.number ?? '?'} ${m?.title || id}` };
    });
  const stages = mission.stages ?? [];
  const stageOpts = stages.flatMap((st, i) =>
    st.id
      ? [{ value: i === 0 ? '' : st.id, label: i === 0 ? `${st.id} (first stage)` : st.id }]
      : [],
  );
  const objectivesOf = (stageId: string) => {
    const st = stageId ? stages.find((x) => x.id === stageId) : stages[0];
    const sc = (bundle.scenarios ?? []).find((x) => x?.id && x.id === st?.encounter?.scenarioId);
    return (sc?.objectives ?? []).flatMap((o) =>
      o.id ? [{ value: o.id, label: `${o.id}${o.label ? `: ${o.label}` : ''}` }] : [],
    );
  };

  const loc = withDefaults(locationRuleDefaults(), s.location ?? {}) as Required<LocationRuleDef>;
  const setLoc = (patch: Partial<LocationRuleDef>) =>
    set({ location: { ...s.location, ...patch } });
  const cover = withDefaults(coverDefaults(), s.cover ?? {}) as ReturnType<typeof coverDefaults>;
  const setCover = (patch: Partial<typeof cover>) => set({ cover: { ...s.cover, ...patch } });
  const rewards = withDefaults(storyRewardsDefaults(), s.rewards ?? {}) as ReturnType<
    typeof storyRewardsDefaults
  >;

  const cellCtx: CellContext = {
    roles: [...castIds, 'hero'], // the cover validator's cast roles (ContentValidator.Story)
    vars: [],
    strings: {},
    setString: () => {},
  };
  const shownPanel =
    previewPanel >= 0 ? (cover.variants[previewPanel]?.panel ?? null) : cover.panel;

  const variantsEditor = (k: 'intro' | 'outro', label: string, hint: string) => (
    <ListEditor<StoryBeatVariantDef>
      label={label}
      hint={hint}
      what="variant"
      items={s[k] ?? []}
      issues={at(k)}
      make={() => ({ when: null, beatId: '' })}
      onChange={(v) => set({ [k]: v })}
      row={(v, i, put) => (
        <div className="grid gap-2 sm:grid-cols-2">
          <SelectField
            label="Beat chain"
            value={v.beatId}
            options={chainOpts}
            issues={at(`${k}.${i}.beatId`)}
            onChange={(x) => put({ ...v, beatId: x ?? '' })}
          />
          <ConditionPicker
            label="When"
            value={v.when}
            issues={relErrors(errors, `${B}.${k}.${i}.when`)}
            onChange={(c) => put({ ...v, when: c })}
          />
        </div>
      )}
    />
  );

  return (
    <div data-testid="issue-story-editor">
      <Fieldset legend="Issue">
        <SelectField
          label="Arc"
          value={s.arcId}
          options={arcs.map((a) => ({ value: a.id ?? '', label: `${a.title || a.id} (${a.id})` }))}
          hint="The arc must also list this mission in its issues (Arcs editor)."
          issues={at('arcId')}
          onChange={(v) => set({ arcId: v ?? '' })}
        />
        <NumberField
          label="Number"
          integer
          min={1}
          value={s.number}
          hint="1-based, unique in the arc."
          issues={at('number')}
          onChange={(v) => set({ number: v ?? 0 })}
        />
        <TextField
          label="Kicker"
          value={s.kicker}
          hint='The shelf&apos;s mission-type line ("Occupied building").'
          issues={at('kicker')}
          onChange={(v) => set({ kicker: v })}
        />
        <NumberField
          label="Minutes"
          integer
          min={0}
          value={s.minutes}
          hint="Read time on the cover card (10–20)."
          issues={at('minutes')}
          onChange={(v) => set({ minutes: v ?? 0 })}
        />
        {arc && !(arc.issues ?? []).includes(mission.id ?? '') && (
          <p className="text-xs text-blam sm:col-span-2">
            Arc {arc.id} doesn&rsquo;t list {mission.id} in its issues yet.
          </p>
        )}
        <CheckboxField
          label="Story issue"
          hint="Untick to make this a plain mission again (removes the story block)."
          checked
          onChange={(on) => {
            if (!on && confirm('Remove the story block from this mission?')) onChange(null);
          }}
        />
      </Fieldset>

      <Fieldset legend="Cast">
        <SelectField
          label="Hero (cast lead)"
          value={s.castLead}
          options={castOpts}
          hint="Hand-controlled and the squad leader."
          issues={at('castLead')}
          onChange={(v) => set({ castLead: v ?? '' })}
        />
        <ListEditor<IssueCastDef>
          label="Who comes along"
          hint="Each when its condition holds (Jojo if freed, Rook if Warned)."
          what="cast member"
          items={s.cast ?? []}
          issues={at('cast')}
          make={() => issueCastDefaults()}
          onChange={(v) => set({ cast: v })}
          row={(c, i, put) => (
            <div className="grid gap-2 sm:grid-cols-2">
              <SelectField
                label="Cast member"
                value={c.castId}
                options={castOpts}
                issues={at(`cast.${i}.castId`)}
                onChange={(x) => put({ ...c, castId: x ?? '' })}
              />
              <CheckboxField
                label="Essential"
                hint="Their death counts as a rewind."
                checked={!!c.essential}
                onChange={(on) => put({ ...c, essential: on })}
              />
              <ConditionPicker
                label="Comes along when"
                value={c.when}
                issues={relErrors(errors, `${B}.cast.${i}.when`)}
                onChange={(w) => put({ ...c, when: w })}
              />
              {c.loadout == null ? (
                <button
                  type="button"
                  className={smallBtn}
                  onClick={() => {
                    const def = arc?.cast?.find((x) => x.id === c.castId)?.loadout;
                    put({ ...c, loadout: structuredClone(def ?? {}) });
                  }}
                >
                  Override their loadout
                </button>
              ) : (
                <>
                  <LoadoutEditor
                    label="Loadout override"
                    value={c.loadout}
                    errors={errors}
                    base={`${B}.cast.${i}.loadout`}
                    onChange={(l) => put({ ...c, loadout: l })}
                  />
                  <button
                    type="button"
                    className={smallBtn}
                    onClick={() => put({ ...c, loadout: null })}
                  >
                    Use the cast default
                  </button>
                </>
              )}
            </div>
          )}
        />
      </Fieldset>

      <Fieldset legend="The hero's loadout">
        <LoadoutEditor
          label="Loadout"
          value={s.loadout}
          errors={errors}
          base={`${B}.loadout`}
          onChange={(l) => set({ loadout: l })}
        />
      </Fieldset>

      <Fieldset legend="Where (location rule)">
        <SelectField
          label="Kind"
          value={loc.kind}
          options={opts(STORY_LOCATION_KINDS)}
          hint="place: a building type · sameAs: an earlier issue's building · picked: the server picks one · anywhere: comic-only."
          issues={at('location.kind')}
          onChange={(v) => setLoc({ kind: v ?? 'place' })}
        />
        <p className="self-end text-xs">
          Cover strip: <strong>{wherePreview(loc, bundle)}</strong>
        </p>
        {(loc.kind === 'place' || loc.kind === 'picked') && (
          <>
            <StringListEditor
              label="OSM kinds"
              value={loc.kinds}
              errors={errors}
              base={`${B}.location.kinds`}
              placeholder="hospital"
              hint="buildings.kind: hospital, clinic, fuel, supermarket…"
              onChange={(v) => setLoc({ kinds: v })}
            />
            <StringListEditor
              label="Categories"
              value={loc.categories}
              errors={errors}
              base={`${B}.location.categories`}
              placeholder="food"
              hint="Building categories that also qualify."
              onChange={(v) => setLoc({ categories: v })}
            />
            <NumberField
              label="Min area m²"
              min={0}
              value={loc.minAreaM2}
              issues={at('location.minAreaM2')}
              onChange={(v) => setLoc({ minAreaM2: v ?? 0 })}
            />
            <NumberField
              label="Min rooms"
              integer
              min={0}
              value={loc.minRooms}
              issues={at('location.minRooms')}
              onChange={(v) => setLoc({ minRooms: v ?? 0 })}
            />
          </>
        )}
        {loc.kind === 'sameAs' && (
          <>
            <SelectField
              label="Same building as"
              value={loc.issue}
              options={arcIssueOpts}
              issues={at('location.issue')}
              onChange={(v) => setLoc({ issue: v ?? '' })}
            />
            <NumberField
              label="Fallback km"
              min={0}
              value={loc.fallbackKm}
              hint="Farther than this, any building matching that issue's rule (0 = storyRules.sameAsFallbackKm)."
              issues={at('location.fallbackKm')}
              onChange={(v) => setLoc({ fallbackKm: v ?? 0 })}
            />
          </>
        )}
        {loc.kind === 'picked' && (
          <>
            <SelectField
              label="Pick near"
              value={loc.pickNear}
              options={opts(STORY_PICK_NEAR)}
              issues={at('location.pickNear')}
              onChange={(v) => setLoc({ pickNear: v ?? 'home' })}
            />
            <SelectField
              label="Signs flag"
              value={loc.signsFlag}
              none="none"
              options={flagOpts}
              hint="The world flag whose signs mark the picked building."
              issues={at('location.signsFlag')}
              onChange={(v) => setLoc({ signsFlag: v ?? '' })}
            />
          </>
        )}
        <IssueList issues={at('location')} />
      </Fieldset>

      <Fieldset legend="Cover">
        <div className="flex flex-wrap gap-4 sm:col-span-2">
          <CoverPreview
            arc={arc}
            number={s.number}
            cover={cover}
            panel={shownPanel}
            fallbackTitle={mission.title ?? ''}
            where={wherePreview(loc, bundle)}
            dogEars={(s.checkpoints ?? []).length}
          />
          <div className="grid min-w-0 flex-1 content-start gap-2">
            <TextField
              label="Title burst"
              value={cover.title}
              issues={at('cover.title')}
              onChange={(v) => setCover({ title: v })}
            />
            <TextField
              label="Tagline"
              value={cover.tagline}
              issues={at('cover.tagline')}
              onChange={(v) => setCover({ tagline: v })}
            />
            <TextField
              label="Masthead kicker"
              value={cover.kicker}
              hint="DON'T OPEN · DEAD INSIDE"
              issues={at('cover.kicker')}
              onChange={(v) => setCover({ kicker: v })}
            />
            <SelectField
              label="Preview panel"
              value={String(previewPanel)}
              options={[
                { value: '-1', label: 'Default panel' },
                ...cover.variants.map((_, i) => ({ value: String(i), label: `Variant ${i + 1}` })),
              ]}
              onChange={(v) => setPreviewPanel(Number(v ?? -1))}
            />
          </div>
        </div>
        <div className="sm:col-span-2">
          {cover.panel == null ? (
            <button
              type="button"
              className={smallBtn}
              onClick={() => setCover({ panel: newCoverPanel(s.castLead) })}
            >
              Add a cover panel
            </button>
          ) : (
            <details open>
              <summary className="cursor-pointer text-sm font-semibold">Default panel</summary>
              <CellEditor
                value={cover.panel}
                ctx={cellCtx}
                errors={errors}
                base={`${B}.cover.panel`}
                onChange={(p) => setCover({ panel: p })}
              />
              <button type="button" className={smallBtn} onClick={() => setCover({ panel: null })}>
                Remove the panel (text-only cover)
              </button>
            </details>
          )}
        </div>
        <ListEditor<CoverVariantDef>
          label="Panel variants"
          hint="First match wins, before the default panel (Mara freed or not)."
          what="variant"
          items={cover.variants}
          issues={at('cover.variants')}
          make={() => ({ when: null, panel: newCoverPanel(s.castLead) })}
          onChange={(v) => setCover({ variants: v })}
          row={(v, i, put) => (
            <details>
              <summary className="cursor-pointer text-sm">Variant {i + 1}</summary>
              <ConditionPicker
                label="When"
                value={v.when}
                issues={relErrors(errors, `${B}.cover.variants.${i}.when`)}
                onChange={(c) => put({ ...v, when: c })}
              />
              {v.panel && (
                <CellEditor
                  value={v.panel}
                  ctx={cellCtx}
                  errors={errors}
                  base={`${B}.cover.variants.${i}.panel`}
                  onChange={(p) => put({ ...v, panel: p })}
                />
              )}
            </details>
          )}
        />
      </Fieldset>

      <Fieldset legend="Intro and outro">
        {variantsEditor(
          'intro',
          'Intro variants',
          'First match wins; the last usually has no condition.',
        )}
        {variantsEditor(
          'outro',
          'Outro variants',
          "First match wins. The arc's last issue plays the ending's beat instead when it has one.",
        )}
      </Fieldset>

      <Fieldset legend="Dog-ears (checkpoints)">
        <ListEditor<StoryCheckpointDef>
          label="Checkpoints"
          hint="2–4. Each on an objective of the stage's scenario, or the stage start."
          what="checkpoint"
          items={s.checkpoints ?? []}
          issues={at('checkpoints')}
          make={() => checkpointDefaults(`cp${(s.checkpoints ?? []).length + 1}`)}
          onChange={(v) => set({ checkpoints: v })}
          row={(c, i, put) => (
            <div className="grid gap-2 sm:grid-cols-4">
              <TextField
                label="Id"
                mono
                value={c.id}
                issues={at(`checkpoints.${i}.id`)}
                onChange={(x) => put({ ...c, id: x })}
              />
              <SelectField
                label="Stage"
                value={c.stage ?? ''}
                none={stageOpts.length ? undefined : 'first stage'}
                options={stageOpts}
                issues={at(`checkpoints.${i}.stage`)}
                onChange={(x) => put({ ...c, stage: x ?? '' })}
              />
              <SelectField
                label="Objective"
                value={c.objective ?? ''}
                none="stage start"
                options={objectivesOf(c.stage ?? '')}
                issues={at(`checkpoints.${i}.objective`)}
                onChange={(x) => put({ ...c, objective: x ?? '' })}
              />
              <TextField
                label="Label"
                value={c.label}
                hint='"the records room"'
                issues={at(`checkpoints.${i}.label`)}
                onChange={(x) => put({ ...c, label: x })}
              />
            </div>
          )}
        />
      </Fieldset>

      <Fieldset legend="Rewards">
        <StackListEditor
          label="Care package (once per issue per run)"
          value={rewards.perRun}
          errors={errors}
          base={`${B}.rewards.perRun`}
          hint="Small, delivered home, capped by the grant validator."
          onChange={(v) => set({ rewards: { ...rewards, perRun: v } })}
        />
        <StackListEditor
          label="On-location bonus (ADR 0062)"
          value={rewards.onLocation ?? []}
          errors={errors}
          base={`${B}.rewards.onLocation`}
          hint="Added to the care package when the issue is played at a matching place (a signed, plausible fix from MISSIONS). Same once-per-run gate."
          onChange={(v) => set({ rewards: { ...rewards, onLocation: v } })}
        />
        <SelectField
          label="First read: cover poster"
          value={rewards.firstRead}
          none="none"
          options={cosmeticOpts}
          issues={at('rewards.firstRead')}
          onChange={(v) => set({ rewards: { ...rewards, firstRead: v ?? '' } })}
        />
      </Fieldset>
    </div>
  );
}
